<template>
  <Transition name="modal-fade">
    <!-- Modal Wrapper -->
    <div
      v-if="isVisible"
      class="fixed inset-0 z-[99999999] flex items-center justify-center overflow-y-auto"
    >
      <!-- Overlay -->
      <div
        class="absolute inset-0 bg-black bg-opacity-50"
        @click="closeModal"
      ></div>

      <!-- Modal Content -->
      <div
        class="modal-content relative bg-white rounded-lg shadow-lg p-10 mobile:p-2 w-[65%] laptop:w-[80%] tablet:w-[80%] mobile:w-[95%] transform transition-all my-8 mx-auto dark:bg-neutral-800 dark:text-neutral-200"
        :class="{
          'translate-y-0 opacity-100': isVisible,
          'translate-y-4 opacity-0': !isVisible,
        }"
        @click.stop
      >
        <!-- Modal Header -->
        <div class="flex justify-between items-center pb-4 mobile:pt-5">
          <h3
            class="text-xl font-semibold mobile:text-center w-full text-right"
          >
            {{ title }}
          </h3>
          <button
            @click="closeModal"
            class="text-gray-500 text-xl transition-colors hover:text-red-500"
          >
            <Icon icon="mdi:close" />
          </button>
        </div>

        <!-- Modal Body -->
        <div class="mt-4">
          <slot></slot>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { Icon } from "@iconify/vue";
import { defineProps, defineEmits, onBeforeUnmount, watch } from "vue";

const props = defineProps({
  isVisible: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["close"]);

const closeModal = () => {
  emit("close");
};

// Prevent scrolling the background when modal is open
const toggleBodyScroll = (disable) => {
  document.body.style.overflow = disable ? "hidden" : "";
};

// Close modal when the 'Esc' key is pressed
const handleEscKey = (event) => {
  if (event.key === "Escape") {
    closeModal();
  }
};

const addKeyListener = () => {
  window.addEventListener("keydown", handleEscKey);
};

const removeKeyListener = () => {
  window.removeEventListener("keydown", handleEscKey);
};

// Watch `isVisible` to handle scroll lock and event listeners
watch(
  () => props.isVisible,
  (newVal) => {
    if (newVal) {
      addKeyListener();
      toggleBodyScroll(true);
    } else {
      removeKeyListener();
      toggleBodyScroll(false);
    }
  }
);

// Cleanup on component unmount
onBeforeUnmount(() => {
  removeKeyListener();
  toggleBodyScroll(false);
});
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.modal-content {
  max-height: calc(100vh - 4rem); /* Subtract margin from viewport height */
  overflow-y: auto;
  overscroll-behavior: contain;
}
</style>
